import { useState, useCallback } from "react";
import Cropper, { Area } from "react-easy-crop";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Upload, X, Crop } from "lucide-react";
import { toast } from "sonner";

interface ImageUploadWithCropProps {
  currentImage: string;
  fallbackText: string;
  userId?: string;
  onImageChange: (url: string) => void;
  label?: string;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.crossOrigin = "anonymous";
    image.src = url;
  });

const CANVAS_SIZE = 500;
const CROPPER_SIZE = 350; // matches the h-[350px] container

async function renderCroppedImage(
  imageSrc: string,
  crop: { x: number; y: number },
  zoom: number
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2d context");

  // White background for padding when zoomed out
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  const { naturalWidth, naturalHeight } = image;

  // react-easy-crop fits the image to cover the crop area.
  // We need to replicate that scaling to match the preview.
  const imgAspect = naturalWidth / naturalHeight;
  // The cropper area is square (aspect=1), so the "cover" base scale
  // makes the shorter dimension fill the cropper.
  let baseScale: number;
  if (imgAspect >= 1) {
    // landscape: height fits the cropper
    baseScale = CROPPER_SIZE / naturalHeight;
  } else {
    // portrait: width fits the cropper
    baseScale = CROPPER_SIZE / naturalWidth;
  }

  const scale = baseScale * zoom;
  const canvasScale = CANVAS_SIZE / CROPPER_SIZE;

  const drawWidth = naturalWidth * scale * canvasScale;
  const drawHeight = naturalHeight * scale * canvasScale;
  const x = (CANVAS_SIZE - drawWidth) / 2 + crop.x * canvasScale;
  const y = (CANVAS_SIZE - drawHeight) / 2 + crop.y * canvasScale;

  ctx.drawImage(image, x, y, drawWidth, drawHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas is empty"));
    }, "image/jpeg", 0.9);
  });
}

const ImageUploadWithCrop = ({
  currentImage,
  fallbackText,
  userId,
  onImageChange,
  label = "Photo / Logo",
}: ImageUploadWithCropProps) => {
  const [uploading, setUploading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Image size must be less than 100MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);

    // Reset file input so the same file can be re-selected
    e.target.value = "";
  };

  const handleCropSave = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    setUploading(true);
    try {
      const croppedBlob = await renderCroppedImage(imageToCrop, crop, zoom);

      // Remove old image from storage if it's from our bucket
      if (currentImage && currentImage.includes("member-logos")) {
        const oldPath = currentImage.split("/").slice(-2).join("/");
        await supabase.storage.from("member-logos").remove([oldPath]);
      }

      const fileName = `${userId || "shared"}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from("member-logos")
        .upload(fileName, croppedBlob, { contentType: "image/jpeg" });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("member-logos")
        .getPublicUrl(fileName);

      onImageChange(publicUrl);
      setCropDialogOpen(false);
      setImageToCrop(null);
      toast.success("Image uploaded successfully!");
    } catch (error: any) {
      toast.error("Error uploading image: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!currentImage) return;
    try {
      if (currentImage.includes("member-logos")) {
        const imagePath = currentImage.split("/").slice(-2).join("/");
        await supabase.storage.from("member-logos").remove([imagePath]);
      }
      onImageChange("");
      toast.success("Image removed!");
    } catch (error: any) {
      toast.error("Error removing image: " + error.message);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <Avatar className="h-24 w-24">
          <AvatarImage src={currentImage} alt={fallbackText} />
          <AvatarFallback className="text-2xl">
            {fallbackText.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              onClick={() => document.getElementById("image-upload-crop")?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {currentImage ? "Change" : "Upload"}
            </Button>
            {currentImage && (
              <Button type="button" variant="outline" size="sm" onClick={handleRemoveImage}>
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">JPG, PNG, or WebP. Max 100MB.</p>
        </div>
        <input
          id="image-upload-crop"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      <Dialog open={cropDialogOpen} onOpenChange={(open) => { if (!open && !uploading) { setCropDialogOpen(false); setImageToCrop(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="h-5 w-5" />
              Frame Your Image
            </DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-[350px] bg-muted rounded-lg overflow-hidden">
            {imageToCrop && (
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Zoom</Label>
            <Slider
              value={[zoom]}
              min={0.5}
              max={3}
              step={0.05}
              onValueChange={(vals) => setZoom(vals[0])}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCropDialogOpen(false); setImageToCrop(null); }} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleCropSave} disabled={uploading}>
              {uploading ? "Saving..." : "Save Image"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImageUploadWithCrop;
